import React, { useState, useEffect } from 'react';
import { usersAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import StateSelect from '../components/StateSelect';
import './ProfilePage.css';

function ProfilePage() {
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    zip_code: '',
    email_notifications: true,
    sms_notifications: true,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [licensePlateUrl, setLicensePlateUrl] = useState(null);

  useEffect(() => {
    loadProfile();
  }, []);

  const loadProfile = async () => {
    try {
      const response = await usersAPI.getProfile();
      setFormData(response.data);

      // Load license plate URL if user is a driver and has a photo
      if (response.data.role === 'driver' && response.data.license_plate_photo) {
        try {
          const urlResponse = await usersAPI.getLicensePlateUrl(response.data.id);
          setLicensePlateUrl(urlResponse.data.url);
        } catch (error) {
          console.log('Could not load license plate URL:', error);
        }
      }
    } catch (error) {
      console.error('Error loading profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage('');

    try {
      await usersAPI.updateProfile(formData);
      setMessage('Profile updated successfully!');
    } catch (error) {
      setMessage('Error updating profile');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="loading">Loading...</div>;

  return (
    <div className="page">
      <h1 className="page-title">My Profile</h1>

      <div className="card">
        <form onSubmit={handleSubmit}>
          <div className="form-row">
            <div className="form-group">
              <label>First Name</label>
              <input
                type="text"
                name="first_name"
                value={formData.first_name}
                onChange={handleChange}
                required
              />
            </div>

            <div className="form-group">
              <label>Last Name</label>
              <input
                type="text"
                name="last_name"
                value={formData.last_name}
                onChange={handleChange}
                required
              />
            </div>
          </div>

          <div className="form-group">
            <label>Email</label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
              disabled
            />
          </div>

          <div className="form-group">
            <label>Phone</label>
            <input
              type="tel"
              name="phone"
              value={formData.phone || ''}
              onChange={handleChange}
              required
            />
          </div>

          {formData.role !== 'driver' && (
            <>
              <h3>Delivery Address</h3>

              <div className="form-group">
                <label>Street Address</label>
                <input
                  type="text"
                  name="address"
                  value={formData.address || ''}
                  onChange={handleChange}
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>City</label>
                  <input
                    type="text"
                    name="city"
                    value={formData.city || ''}
                    onChange={handleChange}
                  />
                </div>

                <div className="form-group">
                  <label>State</label>
                  <StateSelect
                    id="state"
                    name="state"
                    value={formData.state || ''}
                    onChange={handleChange}
                  />
                </div>

                <div className="form-group">
                  <label>ZIP Code</label>
                  <input
                    type="text"
                    name="zip_code"
                    value={formData.zip_code || ''}
                    onChange={handleChange}
                  />
                </div>
              </div>
            </>
          )}

          <h3>Notification Preferences</h3>

          <div className="form-group">
            <label>
              <input
                type="checkbox"
                name="email_notifications"
                checked={formData.email_notifications}
                onChange={handleChange}
              />
              {' '}Email Notifications
            </label>
          </div>

          <div className="form-group">
            <label>
              <input
                type="checkbox"
                name="sms_notifications"
                checked={formData.sms_notifications}
                onChange={handleChange}
              />
              {' '}SMS Notifications
            </label>
          </div>

          {message && (
            <div className={message.includes('success') ? 'success' : 'error'}>
              {message}
            </div>
          )}

          <button type="submit" className="save-changes-btn" disabled={saving}>
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </form>

        {/* Driver License Plate Photo Section */}
        {formData.role === 'driver' && formData.license_plate_photo && (
          <div style={{ marginTop: '30px', paddingTop: '30px', borderTop: '1px solid #e0e0e0' }}>
            <h3>License Plate Photo</h3>
            {licensePlateUrl ? (
              <div style={{ marginTop: '15px' }}>
                <img
                  src={licensePlateUrl}
                  alt="License plate"
                  style={{
                    maxWidth: '400px',
                    width: '100%',
                    borderRadius: '8px',
                    border: '1px solid #ddd',
                    display: 'block'
                  }}
                />
                <p style={{ marginTop: '10px', color: '#666', fontSize: '0.9rem' }}>
                  Stored securely with encryption at rest
                </p>
              </div>
            ) : (
              <p>Loading license plate photo...</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default ProfilePage;
